import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import { useProductSkus } from '../../lib/product-skus-api';
import { LotPositionPicker } from '../../components/LotPositionPicker';

export interface FinishedBoxLineDraft {
  key: string;
  productSkuId: string;
  productSkuLabel: string;
  lotPositionId: string | null;
  lotPositionLabel: string | null;
  qtyBoxesCounted: string;
  qtyBoxesSystem: number | null;
  delta: number | null;
  notes: string;
}

export function InvFinishedBoxSection({
  value,
  onChange,
  readOnly,
}: {
  value: FinishedBoxLineDraft[];
  onChange: (next: FinishedBoxLineDraft[]) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  const addRow = () =>
    onChange([
      ...value,
      {
        key: crypto.randomUUID(),
        productSkuId: '',
        productSkuLabel: '',
        lotPositionId: null,
        lotPositionLabel: null,
        qtyBoxesCounted: '',
        qtyBoxesSystem: null,
        delta: null,
        notes: '',
      },
    ]);

  const updateRow = (key: string, patch: Partial<FinishedBoxLineDraft>) =>
    onChange(value.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const removeRow = (key: string) =>
    onChange(value.filter((r) => r.key !== key));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('inv.sections.finishedBoxLines')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">{t('inv.finishedBoxLines.hint')}</p>
        {value.length === 0 && (
          <p className="text-sm text-slate-500">{t('inv.finishedBoxLines.empty')}</p>
        )}
        {value.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-2 py-2">{t('inv.finishedBoxLines.col.sku')}</th>
                  <th className="px-2 py-2">{t('inv.finishedBoxLines.col.lotPosition')}</th>
                  <th className="px-2 py-2 text-right">{t('inv.finishedBoxLines.col.counted')}</th>
                  <th className="px-2 py-2 text-right">{t('inv.finishedBoxLines.col.system')}</th>
                  <th className="px-2 py-2 text-right">{t('inv.finishedBoxLines.col.delta')}</th>
                  <th className="px-2 py-2">{t('inv.finishedBoxLines.col.notes')}</th>
                  {!readOnly && <th className="px-2 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {value.map((row) => (
                  <BoxRow
                    key={row.key}
                    row={row}
                    onChange={(patch) => updateRow(row.key, patch)}
                    onRemove={() => removeRow(row.key)}
                    readOnly={readOnly}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!readOnly && (
          <Button variant="secondary" size="sm" onClick={addRow}>
            + {t('inv.finishedBoxLines.addRow')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function BoxRow({
  row,
  onChange,
  onRemove,
  readOnly,
}: {
  row: FinishedBoxLineDraft;
  onChange: (patch: Partial<FinishedBoxLineDraft>) => void;
  onRemove: () => void;
  readOnly: boolean;
}) {
  const allSkus = useProductSkus({ active: true });
  const filteredSkus = allSkus.data?.items ?? [];

  // Local computed delta (cliente-side, mentre l'utente digita)
  const counted = parseInt(row.qtyBoxesCounted, 10);
  const computedDelta =
    Number.isFinite(counted) && row.qtyBoxesSystem !== null
      ? counted - row.qtyBoxesSystem
      : null;

  return (
    <tr className="border-b border-slate-100">
      <td className="px-2 py-1">
        {readOnly ? (
          <span className="font-mono text-xs">{row.productSkuLabel}</span>
        ) : (
          <select
            className="block w-44 rounded border border-slate-300 bg-white px-1 py-1 text-xs"
            value={row.productSkuId}
            onChange={(e) => {
              const sku = filteredSkus.find((s) => s.id === e.target.value);
              onChange({
                productSkuId: e.target.value,
                productSkuLabel: sku ? `${sku.sku} (${sku.grammatura}g)` : '',
              });
            }}
          >
            <option value="">—</option>
            {filteredSkus.slice(0, 200).map((s) => (
              <option key={s.id} value={s.id}>
                {s.sku} {s.productName.slice(0, 18)}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="px-2 py-1">
        {readOnly ? (
          <span className="text-xs">{row.lotPositionLabel ?? '— qualsiasi —'}</span>
        ) : (
          <LotPositionPicker
            value={row.lotPositionId ?? ''}
            valueLabel={row.lotPositionLabel ?? undefined}
            minResidualQty={0}
            onChange={(it) =>
              onChange({
                lotPositionId: it.id,
                lotPositionLabel: `${it.lotCode} | ${it.productCode}`,
              })
            }
          />
        )}
      </td>
      <td className="px-2 py-1 text-right">
        {readOnly ? (
          <span>{row.qtyBoxesCounted}</span>
        ) : (
          <Input
            className="w-20 text-right"
            type="number"
            inputMode="numeric"
            value={row.qtyBoxesCounted}
            onChange={(e) => onChange({ qtyBoxesCounted: e.target.value })}
          />
        )}
      </td>
      <td className="px-2 py-1 text-right text-slate-500">
        {row.qtyBoxesSystem ?? '—'}
      </td>
      <td
        className={`px-2 py-1 text-right font-medium ${
          computedDelta === null
            ? 'text-slate-400'
            : computedDelta === 0
              ? 'text-green-700'
              : 'text-amber-700'
        }`}
      >
        {computedDelta !== null ? (computedDelta > 0 ? '+' : '') + computedDelta : '—'}
      </td>
      <td className="px-2 py-1">
        {readOnly ? (
          <span className="text-xs">{row.notes}</span>
        ) : (
          <Input
            className="w-32"
            value={row.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
          />
        )}
      </td>
      {!readOnly && (
        <td className="px-2 py-1">
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-medium text-red-600 hover:underline"
          >
            ✕
          </button>
        </td>
      )}
    </tr>
  );
}
